import { format, startOfDay } from "date-fns";
import { CalendarDays } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { DayButtonProps } from "react-day-picker";
import { cn } from "../lib/utils";
import { PlatformConnection } from "../models/Platform";
import { parseTimeInput } from "../utils/parseTimeInput";
import {
  dayKey,
  dayLoad,
  ScheduleByDate,
  ScheduledSlot,
} from "../utils/scheduleDensity";
import { PlatformConnectionIcon } from "./PlatformConnectionIcon";
import { Calendar } from "./ui/calendar";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

interface SchedulePickerProps {
  /** Epoch ms. */
  value: number;
  onChange: (value: number) => void;
  /** Every already-booked slot, keyed by day. Excludes the post being edited. */
  byDate: ScheduleByDate;
  connectionsById: Record<string, PlatformConnection>;
  /** Whose schedule this is ("LinkedIn (Acme)"). Names the fields for screen readers. */
  label?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Date + time picker for a single connection. Replaces the native datetime-local,
 * whose browser-rendered month grid cannot show what the user already has scheduled —
 * the whole reason people bounced out to the calendar page to reschedule.
 *
 * Colour carries one idea here: brand orange means "something is already booked". It
 * marks the day dots and the taken rows in the time list — nothing else. The selected
 * day stays charcoal; the user already knows where they are.
 */
export function SchedulePicker({
  value,
  onChange,
  byDate,
  connectionsById,
  label,
  disabled,
  className,
}: SchedulePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => new Date(value), [value]);

  // Paging the month must not touch the selected date, so the grid tracks its own month.
  const [month, setMonth] = useState(selected);

  // Where a pointer sent focus when it dismissed the calendar, if anywhere focusable.
  const dismissedTo = useRef<HTMLElement | null>(null);

  const handleDaySelect = (day: Date | undefined) => {
    if (!day) return;
    // The calendar moves the date only; the time of day is the time field's business.
    const next = new Date(day);
    next.setHours(selected.getHours(), selected.getMinutes(), 0, 0);

    // ...except when carrying that time onto today lands in the past — 9:00 AM chosen for
    // tomorrow, then moved to today, at 3 PM. A past timestamp is not a schedule: the
    // server hands it to Cloud Tasks, which fires it immediately.
    onChange(Math.max(next.getTime(), nextQuarterHour()));
    setOpen(false);
  };

  // A new inline arrow here would be a new component *type* on every render, remounting
  // all 42 day cells — which drops keyboard focus mid-navigation whenever a Firestore
  // snapshot re-renders the page. byDate is already memoised upstream.
  const dayComponents = useMemo(
    () => ({
      DayButton: (props: DayButtonProps) => (
        <DayLoadButton {...props} byDate={byDate} />
      ),
    }),
    [byDate]
  );

  return (
    // Date and time are one field, not two boxes that happen to sit together.
    <div
      className={cn(
        "flex items-stretch overflow-hidden rounded-md border border-input bg-card",
        "transition-shadow focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1",
        disabled && "opacity-50",
        className
      )}
    >
      {/* modal: this picker lives inside the Schedule dialog, and a modal Radix Dialog
          puts `pointer-events: none` on <body>. Since the popover portals to body it
          inherits that, so every click lands on the dialog overlay instead — which reads
          as an outside click and shuts the calendar. `modal` restores pointer events to
          the popover layer. (The time list needs the opposite treatment; see below.) */}
      <Popover
        modal
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) {
            setMonth(selected);
            dismissedTo.current = null;
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={label ? `${label} date` : undefined}
            className={cn(
              "flex flex-1 items-center gap-2 px-3 py-2 text-sm",
              "outline-none transition-colors hover:bg-muted/60 disabled:pointer-events-none"
            )}
          >
            <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="font-medium tracking-tight">
              {format(selected, "EEE, MMM d")}
            </span>
          </button>
        </PopoverTrigger>

        {/* Just the grid. There was a per-day agenda here, driven by hover — but content
            you can only reach by hovering is content you can't scroll, and a panel that
            resized itself under the pointer flickered. Which day is busy belongs here;
            what's on it belongs in the time list, where the collision actually matters. */}
        <PopoverContent
          align="start"
          collisionPadding={12}
          // Only a pointer-down actually dismisses a modal layer (Radix suppresses
          // focus-outside dismissal), so this is the one signal that means "the user
          // clicked away". Reading it from onInteractOutside instead would also fire on
          // stray focus events that close nothing, leaving the flag set for a later,
          // legitimate close.
          onPointerDownOutside={(e) => {
            const target = e.target as HTMLElement | null;
            dismissedTo.current = target?.closest(
              "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
            ) as HTMLElement | null;
          }}
          // Being modal, this traps focus and hands it back to the date button on close.
          // That is right for Escape, and wrong when the close was caused by clicking the
          // time field: the click focuses the time input and opens its list, then this
          // focus-restore rips focus back here — and the list, seeing focus land outside
          // it, immediately shuts. Suppress the restore only when the click actually put
          // focus on something else; clicking dead space in the dialog must still hand
          // focus back, or the user is dumped on <body>.
          onCloseAutoFocus={(e) => {
            if (dismissedTo.current) e.preventDefault();
            dismissedTo.current = null;
          }}
          className="w-auto max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-0"
        >
          <Calendar
            mode="single"
            fixedWeeks
            selected={selected}
            month={month}
            onMonthChange={setMonth}
            onSelect={handleDaySelect}
            disabled={{ before: startOfDay(new Date()) }}
            components={dayComponents}
          />
        </PopoverContent>
      </Popover>

      <div aria-hidden className="w-px bg-border" />

      <TimeSelect
        value={value}
        onChange={onChange}
        byDate={byDate}
        connectionsById={connectionsById}
        label={label}
        disabled={disabled}
      />
    </div>
  );
}

/** Every quarter hour of the day, as minutes past midnight. */
const TIME_SLOTS = Array.from({ length: 96 }, (_, i) => i * 15);

/** The soonest slot that hasn't already gone by. */
function nextQuarterHour(): number {
  const next = new Date();
  next.setMinutes(Math.ceil((next.getMinutes() + 1) / 15) * 15, 0, 0);
  return next.getTime();
}

const minutesOf = (time: number): number => {
  const date = new Date(time);
  return date.getHours() * 60 + date.getMinutes();
};

/** A row in the day's timeline: either a time you can pick, or a post already sitting there. */
type TimelineRow =
  | { kind: "slot"; minutes: number; posts: ScheduledSlot[] }
  | { kind: "post"; minutes: number; post: ScheduledSlot };

/**
 * The day as a timeline, in place of `<input type="time">` — whose native popup is both
 * unstylable and blind to the schedule.
 *
 * Existing posts are woven in at their real times. A post at 2:07 PM does not get snapped
 * onto the 2:00 row (that would misreport the schedule this feature exists to reveal); it
 * gets a row of its own between 2:00 and 2:15. A post that does land on a quarter hour
 * annotates that row instead of duplicating it.
 *
 * The input plus the list is an ARIA editable combobox: the caret never leaves the input,
 * and the arrow keys move a virtual cursor (aria-activedescendant) through the options —
 * which is the only way this is operable by keyboard, since tabbing 96 buttons is not.
 */
function TimeSelect({
  value,
  onChange,
  byDate,
  connectionsById,
  label,
  disabled,
}: {
  value: number;
  onChange: (value: number) => void;
  byDate: ScheduleByDate;
  connectionsById: Record<string, PlatformConnection>;
  label?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  // Did a click elsewhere close the list? Then that click owns focus, and we leave it be.
  const closedByOutsideClick = useRef(false);

  const selected = useMemo(() => new Date(value), [value]);
  const selectedMinutes = minutesOf(value);

  // What the user is mid-way through typing. Null means the field just shows the value.
  // The list only offers quarter hours, so typing is the only route to 8:01.
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Where the arrow keys have moved to, independent of what's typed or committed.
  const [cursor, setCursor] = useState<number | null>(null);

  // "Past" is a moving target. Left open while the user writes a caption, a list computed
  // once would keep offering times that have since gone by — and clicking one publishes on
  // the spot. Re-read the clock while the list is open.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!open) return;
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [open]);

  const atMinutes = useCallback(
    (minutes: number): number => {
      const next = new Date(value);
      next.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
      return next.getTime();
    },
    [value]
  );

  /**
   * A time already gone is not a schedule — the server hands a past timestamp straight to
   * Cloud Tasks, which fires it immediately. The datetime-local this replaced carried
   * `min={now}`; this is that guard, and it has to sit on every commit path.
   */
  const isPast = useCallback(
    (time: number): boolean => time <= Math.max(now, Date.now()),
    [now]
  );

  const rows = useMemo<TimelineRow[]>(() => {
    const dayPosts = byDate[dayKey(value)] || [];

    // A hand-typed time won't land on a quarter hour; keep it pickable rather than
    // silently snapping it away.
    const pickable = new Set(TIME_SLOTS);
    pickable.add(selectedMinutes);

    const postsBySlot = new Map<number, ScheduledSlot[]>();
    const offGrid: ScheduledSlot[] = [];
    dayPosts.forEach((post) => {
      const minutes = minutesOf(post.time);
      if (pickable.has(minutes)) {
        postsBySlot.set(minutes, [...(postsBySlot.get(minutes) || []), post]);
      } else {
        offGrid.push(post);
      }
    });

    const slotRows: TimelineRow[] = [...pickable]
      .sort((a, b) => a - b)
      // On the spring-forward day, 2:00–2:45 AM simply do not happen: setHours(2, 30)
      // resolves to 3:30, so those rows would sit there labelled 3:xx, duplicating the
      // real ones and writing an hour later than the row the user clicked. Drop any slot
      // the clock skips over.
      .filter((minutes) => minutesOf(atMinutes(minutes)) === minutes)
      .map((minutes) => ({
        kind: "slot",
        minutes,
        posts: postsBySlot.get(minutes) || [],
      }));
    const postRows: TimelineRow[] = offGrid.map((post) => ({
      kind: "post",
      minutes: minutesOf(post.time),
      post,
    }));

    // Ties can't happen — an off-grid post is by definition not on a slot minute.
    return [...slotRows, ...postRows].sort((a, b) => a.minutes - b.minutes);
  }, [byDate, value, selectedMinutes, atMinutes]);

  /** The options the arrow keys can land on: future quarter hours only. */
  const selectable = useMemo(
    () =>
      rows
        .filter(
          (row): row is Extract<TimelineRow, { kind: "slot" }> =>
            row.kind === "slot" && !isPast(atMinutes(row.minutes))
        )
        .map((row) => row.minutes),
    [rows, isPast, atMinutes]
  );

  // Highlight follows the arrow keys, else what's being typed, else the committed value.
  const draftMinutes = draft === null ? null : parseTimeInput(draft, selectedMinutes);
  const activeMinutes = cursor ?? draftMinutes ?? selectedMinutes;
  const optionId = (minutes: number) => `${listId}-opt-${minutes}`;

  // Bring the active option into view — "nearest", so a row already on screen doesn't
  // lurch under the pointer on every keystroke.
  useEffect(() => {
    if (!open) return;
    document
      .getElementById(optionId(activeMinutes))
      ?.scrollIntoView({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeMinutes]);

  // On opening, land on the current time rather than at midnight.
  useEffect(() => {
    if (!open) return;
    document
      .getElementById(optionId(selectedMinutes))
      ?.scrollIntoView({ block: "center" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setOpen(false);
    setCursor(null);
  };

  const pick = (minutes: number) => {
    const time = atMinutes(minutes);
    // The list is rendered from a clock reading that may be minutes old; re-check on the
    // way out. This is the path the day-select and typing guards don't cover.
    if (isPast(time)) return;
    onChange(time);
    setDraft(null);
    setError(null);
    close();
  };

  /**
   * Nothing is committed while typing. Committing each keystroke meant "08:30" walked the
   * value through midnight (the bare "0"), which flipped the meridiem this parse leans on,
   * and landed on 8:30 AM — while "8:30" landed on 8:30 PM. It also left Escape with
   * nothing to undo, since the value had already moved.
   */
  const commitDraft = (text: string) => {
    const minutes = parseTimeInput(text, selectedMinutes);
    if (minutes === null) {
      setError("Not a time — try 8:30, 08:30, or 20:30.");
      return;
    }
    if (isPast(atMinutes(minutes))) {
      setError("That time has already passed.");
      return;
    }
    onChange(atMinutes(minutes));
    setDraft(null);
    setError(null);
  };

  const moveCursor = (delta: number) => {
    if (selectable.length === 0) return;
    const current = selectable.indexOf(activeMinutes);
    const next =
      current === -1
        ? // Not on an option (a typed odd minute): step to the nearest one in that direction.
          delta > 0
          ? selectable.find((m) => m > activeMinutes) ?? selectable[0]
          : [...selectable].reverse().find((m) => m < activeMinutes) ??
            selectable[selectable.length - 1]
        : selectable[
            Math.min(Math.max(current + delta, 0), selectable.length - 1)
          ];
    setCursor(next);
  };

  return (
    // Not `modal` — a modal popover makes everything outside it inert, including the very
    // input we want to keep typing into. The dialog's `pointer-events: none` on <body> is
    // instead undone by rendering in place (portal={false}).
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setCursor(null);
      }}
    >
      {/* An anchor, not a trigger: the field is a real text input, so the list has to open
          beside it without stealing the caret. Type 8:01, or browse the list — both. */}
      <PopoverAnchor asChild>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          role="combobox"
          aria-label={label ? `${label} time` : "Time"}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open ? optionId(activeMinutes) : undefined}
          aria-invalid={error ? true : undefined}
          aria-errormessage={error ? `${listId}-error` : undefined}
          disabled={disabled}
          value={draft ?? format(selected, "p")}
          // Deliberately NOT onFocus. Closing the list returns focus to this input, and an
          // open-on-focus would then reopen the list it just closed — pick a time, watch it
          // shut and spring back. Click, typing and ArrowDown open it; none of those fire
          // on a programmatic refocus.
          onClick={() => setOpen(true)}
          onChange={(e) => {
            setOpen(true);
            setDraft(e.target.value);
            setCursor(null);
            setError(null);
          }}
          onBlur={(e) => {
            // A click on a row blurs the input before its own click handler runs. Commit
            // here and the row's time would be overwritten by the half-typed draft, and
            // the re-render would move the row out from under the pointer so the click
            // never landed at all. Let the row speak for itself.
            if (listRef.current?.contains(e.relatedTarget as Node)) return;
            if (draft !== null) commitDraft(draft);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              if (!open) setOpen(true);
              moveCursor(1);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              if (!open) setOpen(true);
              moveCursor(-1);
            } else if (e.key === "Home" && open) {
              e.preventDefault();
              setCursor(selectable[0]);
            } else if (e.key === "End" && open) {
              e.preventDefault();
              setCursor(selectable[selectable.length - 1]);
            } else if (e.key === "Enter") {
              e.preventDefault();
              // The arrow keys win if they've been used; otherwise commit what's typed.
              if (cursor !== null) pick(cursor);
              else if (draft !== null) {
                commitDraft(draft);
                close();
              } else close();
            } else if (e.key === "Escape") {
              // Abandon the edit. Only possible because nothing was committed while typing.
              setDraft(null);
              setError(null);
              close();
            }
          }}
          className={cn(
            "w-[7.25rem] bg-transparent px-3 py-2 text-left text-sm tabular-nums",
            "outline-none transition-colors hover:bg-muted/60 disabled:pointer-events-none",
            error && "text-destructive"
          )}
        />
      </PopoverAnchor>

      <PopoverContent
        align="end"
        collisionPadding={12}
        // Keep the caret in the input so the list is a companion to typing, not a modal.
        onOpenAutoFocus={(e) => e.preventDefault()}
        // With an anchor and no trigger, Radix has no element to hand focus back to on
        // close: it falls to <body>, and the dialog's focus scope then sweeps it up to the
        // dialog box, so the next Tab restarts from the top of the form. Put it back in
        // the field the user was working in — unless a click elsewhere closed the list, in
        // which case that click has already said where focus belongs.
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          if (!closedByOutsideClick.current) inputRef.current?.focus();
          closedByOutsideClick.current = false;
        }}
        // The input sits OUTSIDE this content, so Radix's dismiss layer counts the caret
        // resting in it — and the very click that opened the list — as "outside", and
        // shuts the list it just opened. That's the intermittent open-then-close. The
        // anchor is part of this control, so interactions with it are not outside it.
        onPointerDownOutside={(e) => {
          if (inputRef.current?.contains(e.target as Node)) return;
          closedByOutsideClick.current = true;
        }}
        onFocusOutside={(e) => {
          if (inputRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (inputRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
        // Rendered in place rather than portalled to <body>: the Schedule dialog locks
        // scrolling to its own subtree, and a portalled list sits outside it — you could
        // see the times but never wheel through them.
        portal={false}
        className="w-64 p-1"
      >
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label="Times"
          className="max-h-72 overflow-y-auto"
        >
          {rows.map((row) =>
            row.kind === "post" ? (
              <BookedRow
                key={`post-${row.post.postId}-${row.post.time}`}
                post={row.post}
                connectionsById={connectionsById}
              />
            ) : (
              <SlotRow
                key={`slot-${row.minutes}`}
                id={optionId(row.minutes)}
                time={atMinutes(row.minutes)}
                posts={row.posts}
                connectionsById={connectionsById}
                isSelected={row.minutes === selectedMinutes}
                isActive={row.minutes === activeMinutes}
                isPast={isPast(atMinutes(row.minutes))}
                onPick={() => pick(row.minutes)}
              />
            )
          )}
        </div>

        {error && (
          <p
            id={`${listId}-error`}
            role="alert"
            className="border-t px-2 py-1.5 text-xs text-destructive"
          >
            {error}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * A quarter hour you can pick, carrying any post that goes out at exactly this time.
 *
 * aria-disabled rather than disabled: a past slot still has to be announced (otherwise the
 * list appears to start at an arbitrary hour for no stated reason), it just can't be taken.
 * tabIndex -1 because the arrow keys drive this list, not Tab — 96 tab stops is not a
 * control, it's a punishment.
 */
function SlotRow({
  id,
  time,
  posts,
  connectionsById,
  isSelected,
  isActive,
  isPast,
  onPick,
}: {
  id: string;
  time: number;
  posts: ScheduledSlot[];
  connectionsById: Record<string, PlatformConnection>;
  isSelected: boolean;
  isActive: boolean;
  isPast: boolean;
  onPick: () => void;
}) {
  return (
    <div
      id={id}
      role="option"
      tabIndex={-1}
      aria-selected={isSelected}
      aria-disabled={isPast || undefined}
      onClick={isPast ? undefined : onPick}
      className={cn(
        "flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left text-sm",
        "transition-colors hover:bg-muted",
        isActive && !isSelected && "bg-muted",
        isPast && "cursor-not-allowed opacity-40 hover:bg-transparent",
        isSelected && "bg-primary text-primary-foreground hover:bg-primary"
      )}
    >
      <span className="w-[4.75rem] shrink-0 tabular-nums">{format(time, "p")}</span>
      {posts.length > 0 && (
        <PostMark
          posts={posts}
          connectionsById={connectionsById}
          muted={isSelected}
        />
      )}
    </div>
  );
}

/**
 * A post that doesn't sit on a quarter hour. Shown at its true time, and not selectable —
 * it's context, not a choice. Still an option so a screen reader meets it in the list at
 * the right point in the day, rather than never hearing about it.
 */
function BookedRow({
  post,
  connectionsById,
}: {
  post: ScheduledSlot;
  connectionsById: Record<string, PlatformConnection>;
}) {
  return (
    <div
      role="option"
      aria-selected={false}
      aria-disabled
      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-muted-foreground"
    >
      <span className="w-[4.75rem] shrink-0 tabular-nums">
        {format(post.time, "p")}
      </span>
      <PostMark posts={[post]} connectionsById={connectionsById} />
      <span className="sr-only">, already booked</span>
    </div>
  );
}

/** The "taken" marker: who it goes out as, and what it is. */
function PostMark({
  posts,
  connectionsById,
  muted,
}: {
  posts: ScheduledSlot[];
  connectionsById: Record<string, PlatformConnection>;
  muted?: boolean;
}) {
  const connectionIds = posts.flatMap((post) => post.connectionIds);
  const label = posts.length === 1 ? posts[0].label : `${posts.length} posts`;
  const spent = posts.every((post) => post.published);

  return (
    <span className="flex min-w-0 flex-1 items-center gap-1.5">
      <span
        aria-hidden
        className={cn(
          "h-1 w-1 shrink-0 rounded-full",
          muted
            ? "bg-primary-foreground"
            : spent
              ? "bg-muted-foreground/50"
              : "bg-brand-ink"
        )}
      />
      <span className="flex shrink-0 -space-x-1">
        {connectionIds.slice(0, 2).map((id) => {
          const conn = connectionsById[id];
          if (!conn) return null;
          return (
            <PlatformConnectionIcon
              key={id}
              platform={conn.platform}
              profileImage={conn.profileImage}
              profileName={conn.profileName}
              size="xs"
              disableTooltip
            />
          );
        })}
      </span>
      {/* Colour alone would carry "taken"; the word does it for anyone who can't see it. */}
      <span className="sr-only">booked: </span>
      <span
        className={cn("truncate text-xs", muted && "text-primary-foreground/80")}
        title={label}
      >
        {label}
      </span>
    </span>
  );
}

/**
 * Day cell marking whether the day is spoken for. One dot, not a count — the grid says
 * "something is here", the time list says what.
 *
 * Orange means still to go out; grey means the day is behind us. Note that "behind us"
 * is not the same as "published": a post that failed or never fired stays unpublished
 * forever, and colouring by publish status alone lit up months of old failures in orange.
 */
function DayLoadButton({
  byDate,
  ...props
}: DayButtonProps & { byDate: ScheduleByDate }) {
  const { day, modifiers, ...buttonProps } = props;
  const slots = byDate[dayKey(day.date)] || [];
  const { count, upcoming, isHistory } = dayLoad(slots, Date.now());

  const dotClass = isHistory
    ? modifiers.selected
      ? "bg-primary-foreground/50"
      : "bg-muted-foreground/40"
    : modifiers.selected
      ? "bg-brand"
      : "bg-brand-ink";

  // Mirrors the default DayButton, which focuses the day the keyboard moved to.
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  // react-day-picker labels each day ("Saturday, July 18th, 2026"), and an aria-label
  // overrides nested content — so the load has to be appended here or a screen reader
  // never hears it.
  const load =
    count === 0
      ? ""
      : isHistory
        ? `, ${count} past ${count === 1 ? "post" : "posts"}`
        : `, ${upcoming} ${upcoming === 1 ? "post" : "posts"} scheduled`;

  return (
    <button
      ref={ref}
      {...buttonProps}
      aria-label={`${buttonProps["aria-label"] ?? format(day.date, "PPPP")}${load}`}
      className={cn(buttonProps.className, "relative")}
    >
      <span className={cn(count > 0 && "-translate-y-[3px]")}>
        {day.date.getDate()}
      </span>

      {/* On the charcoal selected cell the ink shade goes muddy, so the dot flips to the
          brighter brand — same meaning, legible against either surface. */}
      {count > 0 && (
        <span
          aria-hidden
          className={cn(
            "absolute bottom-[5px] left-1/2 h-1 w-1 -translate-x-1/2 rounded-full",
            dotClass
          )}
        />
      )}
    </button>
  );
}
